/**
 * <PauseSubscriptionFlow> — « Mettre en pause l'abonnement », de bout en bout (template).
 *
 * Deux étapes, comme la modale legacy (`ParentPauseSubscriptionModal.js`) : les dates et le motif,
 * puis le récapitulatif — avec, au-delà du seuil de perte de créneau, l'accord à donner avant de
 * confirmer. Une troisième vue clôt le geste : l'accusé, qui remplace le Snackbar du legacy pour
 * que le parcours finisse comme les autres gestes parent (voir `DECISIONS.md`).
 *
 * Contrôlé comme `<MoveSessionFlow>` : l'étape, les dates, le motif, l'accord, l'envoi en cours et
 * l'échec arrivent en props ; la machine à états vit côté écran. Chaque état — quota atteint,
 * envoi, échec, succès — s'ouvre seul en story.
 *
 * **Aucun chiffre métier ici.** Le quota annuel, la durée minimale, la durée maximale consécutive
 * et le seuil de perte de créneau arrivent déjà en libellés ou en bornes (`UIPauseRecap`,
 * `ackThresholdLabel`, `dateError`, les bornes des calendriers). Le droit à pause RESTANT ne
 * s'affiche pas (Wajih, 30/09) : le montrer pousse aux pauses longues, alors que la limite n'est
 * qu'un garde-fou — elle borne les calendriers et refuse les dates qui la dépassent. Les phrases,
 * elles, sont ICI : c'est le seul endroit où le parent les lit, et elles sont écrites pour lui —
 * vouvoyé, son enfant à la troisième personne.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { PauseDatesStep } from "../../organisms/PauseDatesStep"
import { PauseRecapStep } from "../../organisms/PauseRecapStep"
import { type UIPauseReason, type UIPauseRecap } from "../../../ports/primary/ui/types"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { formatDayMonthYear } from "../../../ports/primary/ui/formatters/dateFormatter"
import { ParentFlowModal } from "../ParentFlowModal"

/** Les deux étapes du legacy, plus l'accusé qui clôt le geste. */
export type PauseSubscriptionStep = "dates" | "recap" | "done"

const TOTAL_STEPS = 2

/**
 * Le repli du legacy quand le serveur refuse sans dire pourquoi
 * (`ParentPauseSubscriptionModal.js:40-41`). Le message du back prime quand il existe : il porte
 * la raison du refus (chevauchement, quota, fenêtre estivale…). C'est au view-model de choisir ;
 * la phrase, elle, appartient au geste.
 */
export const PAUSE_SUBMIT_ERROR_FALLBACK = "Nous n'avons pas pu programmer la pause. Réessayez dans un instant."

export type PauseSubscriptionFlowProps = {
    visible: boolean
    onClose: () => void
    step: PauseSubscriptionStep
    childFirstname: string

    /** `YYYY-MM-DD`, ou `null` tant que rien n'est choisi. */
    startDate: string | null
    onChangeStartDate: (next: string) => void
    startMinDate?: Date
    startMaxDate?: Date
    /** La fenêtre estivale appartient au Programme d'été : aucune pause ne s'y pose. */
    startDateDisabled?: boolean
    /** La limite de l'app que le parent atteint, en une phrase courte, ou rien. */
    limitNote?: string | null
    /** Ouvre l'aide. Absent → la phrase reste, sans lien. */
    onContactSupport?: () => void

    resumeDate: string | null
    onChangeResumeDate: (next: string) => void
    resumeMinDate?: Date
    resumeMaxDate?: Date

    /** « 21 jours » — absent tant que les deux dates ne sont pas posées. */
    durationLabel?: string | null
    /** La phrase qui refuse les dates choisies : durée, quota, ou frontière de saison. */
    dateError?: string | null

    reasons: UIPauseReason[]
    selectedReason?: string | null
    onSelectReason: (value: string) => void

    /** Passe au récapitulatif. */
    onContinue?: () => void

    /** Ce que la pause décale de l'engagement. */
    recap?: UIPauseRecap | null

    /** La pause dépasse le seuil de perte de créneau. */
    ackRequired?: boolean
    /** « 1 mois » — le seuil, tel que le parent le lit. */
    ackThresholdLabel?: string | null
    ackAccepted?: boolean
    onChangeAck?: (next: boolean) => void

    onSubmit?: () => void
    submitting?: boolean
    /** L'échec de l'envoi — le message du serveur, ou `PAUSE_SUBMIT_ERROR_FALLBACK`. */
    errorMessage?: string | null

    /** Retour au choix des dates. Absent → pas de flèche de retour. */
    onBack?: () => void
    testID?: string
}

export function PauseSubscriptionFlow(props: PauseSubscriptionFlowProps) {
    const {
        visible,
        onClose,
        step,
        childFirstname,
        startDate,
        onChangeStartDate,
        startMinDate,
        startMaxDate,
        startDateDisabled = false,
        limitNote,
        onContactSupport,
        resumeDate,
        onChangeResumeDate,
        resumeMinDate,
        resumeMaxDate,
        durationLabel,
        dateError,
        reasons,
        selectedReason,
        onSelectReason,
        onContinue,
        recap,
        ackRequired = false,
        ackThresholdLabel,
        ackAccepted = false,
        onChangeAck,
        onSubmit,
        submitting = false,
        errorMessage,
        onBack,
        testID,
    } = props

    const reasonLabel = reasons.find((reason) => reason.value === selectedReason)?.label ?? ""

    // Tout est déjà dans les props : les dates, le motif, et la phrase de refus qui porte les
    // règles métier. Une prop `canContinue` de plus ne dirait rien que celles-ci ne disent.
    const canContinue =
        startDate != null &&
        resumeDate != null &&
        selectedReason != null &&
        (dateError == null || dateError === "") &&
        !startDateDisabled

    const canSubmit = (!ackRequired || ackAccepted) && !submitting

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />

    const content = (() => {
        switch (step) {
            case "dates":
                return {
                    stepTitle: "Quelles dates ?",
                    stepIndex: 1,
                    footer: (
                        <Stack gap="2">
                            <Button label="Continuer" onPress={onContinue} disabled={!canContinue} fullWidth />
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <PauseDatesStep
                            startDate={startDate}
                            onChangeStartDate={onChangeStartDate}
                            startMinDate={startMinDate}
                            startMaxDate={startMaxDate}
                            startDateDisabled={startDateDisabled}
                            limitNote={limitNote}
                            onContactSupport={onContactSupport}
                            resumeDate={resumeDate}
                            onChangeResumeDate={onChangeResumeDate}
                            resumeMinDate={resumeMinDate}
                            resumeMaxDate={resumeMaxDate}
                            durationLabel={durationLabel}
                            dateError={dateError}
                            reasons={reasons}
                            selectedReason={selectedReason}
                            onSelectReason={onSelectReason}
                        />
                    ),
                }

            case "recap":
                return {
                    stepTitle: "Récapitulatif",
                    stepIndex: 2,
                    footer: (
                        <Stack gap="2">
                            <Button
                                label="Confirmer la pause"
                                onPress={onSubmit}
                                loading={submitting}
                                disabled={!canSubmit}
                                fullWidth
                            />
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <PauseRecapStep
                            childFirstname={childFirstname}
                            startDate={startDate ?? ""}
                            resumeDate={resumeDate ?? ""}
                            durationLabel={durationLabel ?? ""}
                            reasonLabel={reasonLabel}
                            recap={recap}
                            ackRequired={ackRequired}
                            ackThresholdLabel={ackThresholdLabel}
                            ackAccepted={ackAccepted}
                            onChangeAck={onChangeAck}
                            errorMessage={errorMessage}
                        />
                    ),
                }

            case "done":
            default:
                return {
                    stepTitle: "Pause programmée",
                    stepIndex: 2,
                    footer: <Button label="Fermer" variant="outline" onPress={onClose} />,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            message={`La pause ${elide(childFirstname)} a bien été programmée.`}
                            detail={
                                startDate != null && resumeDate != null
                                    ? `Du ${formatDayMonthYear(startDate)}, reprise le ${formatDayMonthYear(resumeDate)}.`
                                    : undefined
                            }
                        />
                    ),
                }
        }
    })()

    return (
        <ParentFlowModal
            visible={visible}
            onClose={onClose}
            title="Mettre en pause l'abonnement"
            stepTitle={content.stepTitle}
            step={content.stepIndex}
            totalSteps={TOTAL_STEPS}
            // Le « Retour » du legacy est la flèche de la tête d'étape : un bouton de plus au pied
            // ferait trois actions sur une étape qui n'en autorise que deux.
            onBack={step === "recap" ? onBack : undefined}
            context={`pour ${childFirstname}`}
            footer={content.footer}
            testID={testID}
        >
            {content.body}
        </ParentFlowModal>
    )
}
